import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Briefcase, Building2, Camera, Check, LayoutGrid, Lock, Mail, User, UserCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useLoading } from "@/lib/loading";
import { useToast } from "@/lib/toast";
import { compressImage, pickImage } from "@/lib/api";
import { FALLBACK_TERMS_VERSION } from "@/lib/legalTerms";
import { Button, Input, Overline } from "@/components/app/Primitives";
import { PhoneInput } from "@/components/app/PhoneInput";
import { TermsModal } from "@/components/app/TermsModal";
import { AuthBackground } from "@/components/app/Layout";

export default function Register() {
  const { signUp } = useAuth();
  const { runWithLoading } = useLoading();
  const toast = useToast();
  const navigate = useNavigate();
  const [f, setF] = useState({ full_name: "", email: "", password: "", confirm: "", designation: "", company: "", workspace_name: "" });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const [mobile, setMobile] = useState("");
  const [mobileValid, setMobileValid] = useState(false);
  const [type, setType] = useState("personal");
  const [avatar, setAvatar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [agreedVersion, setAgreedVersion] = useState(FALLBACK_TERMS_VERSION);
  const [showTerms, setShowTerms] = useState(false);

  const pickAvatar = async () => {
    const file = await pickImage();
    if (!file) return;
    try { const r = await compressImage(file, 512, 0.5, true); setAvatar(r.dataUrl); } catch { toast.show("Could not read image", "error"); }
  };

  const submit = async () => {
    if (!f.full_name.trim() || !f.email.trim() || !f.password) return toast.show("Full name, email and password are required", "error");
    if (f.password.length < 6) return toast.show("Password must be at least 6 characters", "error");
    if (f.password !== f.confirm) return toast.show("Passwords do not match", "error");
    if (!mobile) return toast.show("Mobile number is required — you'll receive an OTP to verify it.", "error");
    if (!mobileValid) return toast.show("Please enter a valid mobile number with country code", "error");
    if (!agreed) return toast.show("Please accept the Terms & Conditions and Privacy Policy to continue.", "error");
    setLoading(true);
    try {
      await runWithLoading(() => signUp({
        full_name: f.full_name.trim(),
        email: f.email.trim().toLowerCase(),
        password: f.password,
        mobile_number: mobile,
        company_name: f.company.trim() || undefined,
        avatar_base64: avatar || undefined,
        workspace_name: f.workspace_name.trim() || undefined,
        workspace_type: type,
        designation: f.designation.trim() || undefined,
        consent_accepted: true,
        consent_version: agreedVersion,
      }), "Creating your account…");
      toast.show("Account created. Please verify your email and mobile.", "success");
      navigate("/verify", { replace: true });
    } catch (e) {
      toast.show(e instanceof Error ? e.message : "Sign up failed", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthBackground>
      <div className="-mt-4 mb-5 flex flex-col items-center gap-1.5">
        <img src="/branding/assignoo-display.png" alt="Assignoo" className="h-[180px] w-[280px] object-contain" data-testid="register-brand-logo" />
        <p className="text-center text-sm text-a-muted">Every user gets their own workspace + code. Invite teammates after signing in.</p>
      </div>

      <div className="mb-5 flex items-center gap-3">
        <button onClick={pickAvatar} data-testid="register-avatar-picker" className="shrink-0">
          {avatar ? (
            <img src={avatar} alt="avatar" className="h-[68px] w-[68px] rounded-full border-2 border-a-primary object-cover" />
          ) : (
            <span className="flex h-[68px] w-[68px] items-center justify-center rounded-full border-2 border-a-border bg-a-dim text-a-primary transition-colors hover:border-a-primary"><Camera size={26} /></span>
          )}
        </button>
        <div className="flex-1">
          <p className="font-bold text-a-text">Profile photo</p>
          <p className="text-xs text-a-muted">Optional. Helps teammates recognize you.</p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <Input testID="register-name-input" label="Full name *" placeholder="Jane Doe" value={f.full_name} onChangeText={set("full_name")} leading={<User size={18} />} />
        <Input testID="register-email-input" label="Email *" type="email" placeholder="you@company.com" value={f.email} onChangeText={set("email")} leading={<Mail size={18} />} />
        <PhoneInput testID="register-mobile-input" label="Mobile number *" placeholder="98765 43210" valueE164={mobile}
          onChangeE164={(e164, ok) => { setMobile(e164); setMobileValid(!!ok); }}
          helper="Pick your country and enter your mobile number. You'll receive an OTP to verify it." />
        <Input testID="register-password-input" label="Password *" type="password" placeholder="6+ characters" value={f.password} onChangeText={set("password")} leading={<Lock size={18} />} />
        <Input testID="register-confirm-password-input" label="Confirm password *" type="password" placeholder="Re-enter password" value={f.confirm} onChangeText={set("confirm")}
          helper={f.confirm.length > 0 && f.confirm !== f.password ? "Passwords do not match" : undefined} leading={<Lock size={18} />} />
        <Input testID="register-designation-input" label="Designation" placeholder="Product Manager" value={f.designation} onChangeText={set("designation")} leading={<Briefcase size={18} />} />
        <Input testID="register-company-input" label="Company (optional)" placeholder="Acme Corp" value={f.company} onChangeText={set("company")} leading={<Building2 size={18} />} />

        <div className="flex flex-col gap-2">
          <Overline>Default workspace type</Overline>
          <div className="flex gap-3">
            {["personal", "company"].map((t) => {
              const active = type === t;
              const Icon = t === "personal" ? UserCircle : Building2;
              return (
                <button key={t} data-testid={`register-type-${t}`} onClick={() => setType(t)}
                  className={`flex flex-1 flex-col items-start gap-2 rounded-[14px] border p-3.5 text-left transition-colors ${active ? "border-a-primary bg-a-dim" : "border-a-border bg-a-surface hover:border-a-primary/50"}`}>
                  <Icon size={22} className={active ? "text-a-primary" : "text-a-muted"} />
                  <span className={`font-bold ${active ? "text-a-primary" : "text-a-text"}`}>{t === "personal" ? "Personal" : "Company"}</span>
                  <span className="text-xs text-a-muted">{t === "personal" ? "Solo + a few teammates" : "Departments, teams, roles"}</span>
                </button>
              );
            })}
          </div>
        </div>

        <Input testID="register-workspace-input" label="Workspace name (optional)" placeholder={type === "personal" ? "My Workspace" : "Acme Corp"}
          value={f.workspace_name} onChangeText={set("workspace_name")} leading={<LayoutGrid size={18} />} />

        <div className="mt-1.5 flex items-start gap-2.5 py-1">
          <button data-testid="register-consent-checkbox" onClick={() => setAgreed((v) => !v)}
            className={`mt-px flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md border-2 transition-colors ${agreed ? "border-a-primary bg-a-primary" : "border-a-border"}`}>
            {agreed ? <Check size={16} className="text-white" strokeWidth={3} /> : null}
          </button>
          <div className="flex-1">
            <p className="text-[13px] leading-[19px] text-a-text">
              I agree to the Assignoo{" "}
              <button data-testid="register-view-terms" onClick={() => setShowTerms(true)} className="font-bold text-a-primary underline">Terms &amp; Conditions and Privacy Policy</button>.
            </p>
            <button onClick={() => setShowTerms(true)} data-testid="register-view-full-agreement" className="mt-1 text-xs font-bold text-a-primary">View Full Agreement →</button>
          </div>
        </div>

        <Button testID="register-submit-button" label="Create account" size="lg" loading={loading} disabled={!agreed} onPress={submit} className="mt-2" />
        <div className="mt-3 flex justify-center gap-2">
          <span className="text-a-muted">Already a member?</span>
          <Link to="/login" replace className="font-bold text-a-primary hover:underline" data-testid="register-login-link">Sign in</Link>
        </div>
      </div>
      <TermsModal visible={showTerms} onClose={() => setShowTerms(false)} onAgree={(v) => { setAgreedVersion(v); setAgreed(true); }} />
    </AuthBackground>
  );
}
